<div [formGroup]="transformPatterForm" class="transform-editor">
    <div class="section-header">
        <div>
            <h3>Data transformation</h3>
            <p class="section-help">
                Transform outputs augment indexed data. The feed's standard field mapping is preserved separately and is not generated from these properties.
            </p>
        </div>
        <button mat-raised-button color="primary" type="button" (click)="createTransformPattern()">
            <mat-icon>add</mat-icon>
            URL pattern
        </button>
    </div>

    @if (!patterns.length) {
        <div class="empty-state">
            <mat-icon>schema</mat-icon>
            <span>No URL patterns configured.</span>
        </div>
    }

    <div formArrayName="patterns" class="pattern-list">
        @for (mapping of patterns.controls; track mapping; let i = $index) {
            <mat-expansion-panel [expanded]="i === 0" class="pattern-panel">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        {{ mapping.get('urlPattern')?.value || 'Untitled URL pattern' }}
                    </mat-panel-title>
                    <mat-panel-description>
                        {{ getTransformMapping(mapping).length }} transform{{ getTransformMapping(mapping).length === 1 ? '' : 's' }}
                    </mat-panel-description>
                </mat-expansion-panel-header>

                <div [formGroupName]="i" class="pattern-body">
                    <div class="pattern-toolbar">
                        <mat-form-field appearance="outline" class="url-pattern-field">
                            <mat-label>URL pattern</mat-label>
                            <input formControlName="urlPattern" matInput placeholder="https://example.com/products/*">
                            @if (mapping.get('urlPattern')?.hasError('required')) {
                                <mat-error>URL pattern is required.</mat-error>
                            }
                        </mat-form-field>
                        <div class="toolbar-actions">
                            <button (click)="duplicatePattern(i)"
                                    aria-label="Duplicate URL pattern"
                                    mat-icon-button type="button"
                                    matTooltip="Duplicate pattern">
                                <mat-icon>content_copy</mat-icon>
                            </button>
                            <button (click)="removeFieldMapping(i)"
                                    aria-label="Remove URL pattern"
                                    color="warn"
                                    mat-icon-button type="button"
                                    matTooltip="Remove pattern">
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                    </div>

                    <div class="transforms" formArrayName="transforms">
                        @for (transform of getTransformMapping(mapping).controls; track transform; let j = $index) {
                            <div [formGroupName]="j" class="transform-row">
                                <div class="transform-row__header">
                                    <div>
                                        <h4>{{ transform.get('name')?.value || 'Unnamed transform' }}</h4>
                                        <span>{{ getTransformSummary(transform) }}</span>
                                    </div>
                                    <div class="toolbar-actions">
                                        <button (click)="duplicateTransform(i, j)"
                                                aria-label="Duplicate transform"
                                                mat-icon-button type="button"
                                                matTooltip="Duplicate transform">
                                            <mat-icon>content_copy</mat-icon>
                                        </button>
                                        <button (click)="removeTransform(i, j)"
                                                aria-label="Remove transform"
                                                color="warn"
                                                mat-icon-button type="button"
                                                matTooltip="Remove transform">
                                            <mat-icon>delete</mat-icon>
                                        </button>
                                    </div>
                                </div>

                                <div class="transform-grid">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Output name</mat-label>
                                        <input formControlName="name" matInput placeholder="price">
                                        @if (transform.get('name')?.hasError('required')) {
                                            <mat-error>Name is required.</mat-error>
                                        }
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Transformer</mat-label>
                                        <mat-select formControlName="transformer">
                                            @for (option of availableTransformerOptions; track option.value) {
                                                <mat-option [value]="option.value">
                                                    {{ option.label }}
                                                </mat-option>
                                            }
                                        </mat-select>
                                        @if (transform.get('transformer')?.hasError('required')) {
                                            <mat-error>Transformer is required.</mat-error>
                                        }
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Primary node</mat-label>
                                        <input formControlName="primaryNode" matInput
                                               [placeholder]="isGoogleMerchantTransform(transform) ? 'Not used' : '//main'">
                                        @if (transform.hasError('primaryNodeRequired')) {
                                            <mat-error>Primary node is required.</mat-error>
                                        }
                                    </mat-form-field>
                                </div>

                                @if (getTransformerOption(transform.get('transformer')?.value)?.hint; as hint) {
                                    <p class="transform-hint">{{ hint }}</p>
                                }

                                <div class="properties" formGroupName="properties">
                                    <div class="properties__header">
                                        <span>Properties</span>
                                        @if (isGoogleMerchantTransform(transform) && !hasAnyGoogleMerchantGroupKeyProperty(transform)) {
                                            <button (click)="addGoogleMerchantGroupKeyConfiguration(i, j)"
                                                    mat-stroked-button
                                                    type="button">
                                                <mat-icon>add</mat-icon>
                                                Configure variant grouping
                                            </button>
                                        } @else if (getSuggestedProperties(transform).length) {
                                            <div class="suggested-properties">
                                                @for (property of getSuggestedProperties(transform); track property) {
                                                    <button (click)="addSuggestedProperty(i, j, property)"
                                                            mat-stroked-button
                                                            type="button">
                                                        <mat-icon>add</mat-icon>
                                                        {{ property }}
                                                    </button>
                                                }
                                            </div>
                                        }
                                    </div>

                                    @if (isGoogleMerchantTransform(transform)) {
                                        <p class="transform-hint">
                                            Variant grouping is optional. If enabled, source field, extraction pattern, and legacy unsafe value are all required. Source field must be <code>id</code> or <code>item_group_id</code>; the pattern must use <code>\A</code>/<code>\z</code> anchors and one named <code>product</code> capture.
                                        </p>
                                    }
                                    @if (transform.hasError('incompleteGoogleMerchantGroupKey')) {
                                        <p class="transform-error" role="alert">Complete all three Google Merchant group-key fields, or remove all three.</p>
                                    }
                                    @if (transform.hasError('invalidGoogleMerchantGroupKeySource')) {
                                        <p class="transform-error" role="alert">GroupKeySourceField must be id or item_group_id.</p>
                                    }
                                    @if (transform.hasError('invalidGoogleMerchantGroupKeyPattern')) {
                                        <p class="transform-error" role="alert">GroupKeyExtractionPattern must be at most 256 characters, use \A and \z anchors, and contain exactly one named product capture.</p>
                                    }

                                    @for (key of getPropertyKeys(transform); track key) {
                                        <div class="property-row">
                                            <mat-form-field appearance="outline">
                                                <mat-label>Property</mat-label>
                                                <input [value]="key" readonly matInput>
                                            </mat-form-field>
                                            <mat-form-field appearance="outline">
                                                <mat-label>Value</mat-label>
                                                <input [formControlName]="key" matInput placeholder="Value">
                                                @if (getPropertiesGroup(transform).get(key)?.hasError('required')) {
                                                    <mat-error>Value is required.</mat-error>
                                                }
                                            </mat-form-field>
                                            <button (click)="removeProperty(i, j, key)"
                                                    aria-label="Remove property"
                                                    color="warn"
                                                    mat-icon-button type="button"
                                                    matTooltip="Remove property">
                                                <mat-icon>delete</mat-icon>
                                            </button>
                                        </div>
                                    }

                                    <div class="property-row property-row--new">
                                        <mat-form-field appearance="outline">
                                            <mat-label>New property</mat-label>
                                            <input [value]="getDraftValue(i, j, 'key')"
                                                   (input)="setDraftValue(i, j, 'key', $any($event.target).value)"
                                                   matInput
                                                   placeholder="Attribute">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Value</mat-label>
                                            <input [value]="getDraftValue(i, j, 'value')"
                                                   (input)="setDraftValue(i, j, 'value', $any($event.target).value)"
                                                   matInput
                                                   placeholder="href">
                                        </mat-form-field>
                                        <button (click)="addCustomProperty(i, j)"
                                                aria-label="Add property"
                                                color="primary"
                                                mat-icon-button type="button"
                                                matTooltip="Add property">
                                            <mat-icon>add</mat-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>

                    <button (click)="createTransform(i)" color="accent" mat-stroked-button type="button">
                        <mat-icon>add</mat-icon>
                        Transform
                    </button>
                </div>
            </mat-expansion-panel>
        }
    </div>
</div>
